import type { HTMLAttributes, ReactNode } from "react";
import type { Bucket } from "../api/types";
import { bucketStyle } from "../theme";
import { useGaugeFill, useHighlight } from "../motion";
import { formatPercent } from "../format";
import { cx } from "./cx";
import styles from "./Stat.module.css";

export type StatProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  /** Ce que le chiffre mesure, précédé de la pastille du seau. */
  label: ReactNode;
  /**
   * Le chiffre, **déjà formaté** (`formatTokens(...)`, `formatEuros(...)`).
   *
   * Pour qu'il monte au lieu d'apparaître :
   * `value={<span ref={useCountUp(balance.available)} />}`.
   */
  value: ReactNode;
  /** Unité après le chiffre : « tokens », « € ». */
  unit?: ReactNode;
  /** Précision sous le chiffre — une part, une date, un rappel. */
  hint?: ReactNode;
  /**
   * Seau du grand livre décrit par cette tuile. Teinte le filet, le halo et la
   * jauge de la couleur du seau — le motif qu'on retrouve partout ailleurs.
   */
  bucket?: Bucket;
  /**
   * Part à représenter, entre 0 et 1 (voir `ratio()` de `format/`). Affiche une
   * jauge qui se remplit. Omis, aucune jauge.
   */
  ratio?: number;
  /** Pictogramme à gauche du libellé. Décoratif, non annoncé. */
  icon?: ReactNode;
  /** Chiffre principal de l'écran : une taille au-dessus. */
  emphasis?: boolean;
  /**
   * Valeur brute surveillée : quand elle change sous les yeux, la tuile s'entoure
   * d'un halo bref de la couleur de son seau (`useHighlight`).
   */
  highlight?: unknown;
  /** Avec `highlight` : briller aussi à l'apparition (un reçu qui arrive). */
  highlightInitial?: boolean;
};

/**
 * Tuile de chiffre clé.
 *
 * C'est la brique du dashboard : une par seau, toutes teintées de la couleur de
 * leur seau. Elle sert aussi ailleurs — total d'un achat, reliquat d'une
 * mission — avec ou sans seau.
 */
export function Stat({
  label,
  value,
  unit,
  hint,
  bucket,
  ratio,
  icon,
  emphasis = false,
  highlight,
  highlightInitial = false,
  className,
  style,
  ...rest
}: StatProps) {
  const halo = useHighlight<HTMLDivElement>(highlight, { initial: highlightInitial });
  // Le hook est appelé sans condition, comme l'exige React ; la jauge, elle,
  // n'est rendue que si une part a été fournie.
  const jauge = useGaugeFill<HTMLDivElement>(ratio ?? 0);

  return (
    <div
      ref={halo}
      className={cx(styles.stat, emphasis && styles.emphase, className)}
      style={{ ...(bucket ? bucketStyle(bucket) : {}), ...style }}
      {...rest}
    >
      <div className={styles.entete}>
        {icon !== undefined && (
          <span className={styles.icone} aria-hidden="true">
            {icon}
          </span>
        )}
        <span className={styles.libelle}>{label}</span>
      </div>

      <div className={styles.ligneValeur}>
        <span className={styles.valeur}>{value}</span>
        {unit !== undefined && <span className={styles.unite}>{unit}</span>}
      </div>

      {hint !== undefined && <p className={styles.mention}>{hint}</p>}

      {ratio !== undefined && (
        <div
          className={styles.piste}
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(ratio * 100)}
          aria-valuetext={formatPercent(ratio * 100)}
        >
          <div ref={jauge} className={styles.remplissage} />
        </div>
      )}
    </div>
  );
}
